import { useState, useEffect, useCallback } from 'react'
import { FolderDown, Download, Copy, Check, Video, Image, FileText, Music, Share2 } from 'lucide-react'
import clsx from 'clsx'
import { apiUrl } from '../config/api'
import PullToRefresh from '../components/ui/PullToRefresh'
import Button from '../components/ui/Button'

interface MediaItem {
  id: number
  title: string
  description: string | null
  type: string
  file_url: string
  file_name: string
  file_size: number
  mime_type: string | null
  caption_text: string | null
  category_name: string | null
  category_color: string | null
  download_count: number
  created_at: string
}

const typeIcons: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  video: Video,
  image: Image,
  audio: Music,
  document: FileText,
}

const typeLabels: Record<string, string> = {
  video: 'Vídeo',
  image: 'Imagem',
  audio: 'Áudio',
  document: 'Documento',
}


type FilterType = 'all' | 'image' | 'video' | 'audio' | 'document'

export default function MateriaisPage() {
  const [items, setItems] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [filter, setFilter] = useState<FilterType>('all')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [copiedId, setCopiedId] = useState<number | null>(null)

  const perPage = 12

  const fetchItems = useCallback(async (pageNum: number, append: boolean) => {
    if (pageNum === 1) setLoading(true)
    else setLoadingMore(true)

    try {
      const token = localStorage.getItem('token')
      const params = new URLSearchParams({ page: String(pageNum), per_page: String(perPage) })
      if (filter !== 'all') params.set('type', filter)

      const res = await fetch(apiUrl(`/v1/community/media?${params}`), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      const newItems: MediaItem[] = data.data || []

      if (append) {
        setItems(prev => [...prev, ...newItems])
      } else {
        setItems(newItems)
      }

      setHasMore(pageNum < (data.meta?.last_page || 1))
    } catch {
      // erro silencioso
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [filter])

  useEffect(() => {
    setPage(1)
    fetchItems(1, false)
  }, [filter, fetchItems])

  // Refresh silencioso quando volta pro app
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') {
        setPage(1)
        fetchItems(1, false)
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchItems])

  function loadMore() {
    const next = page + 1
    setPage(next)
    fetchItems(next, true)
  }

  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  // Detecta se tá dentro do Flutter WebView
  const isFlutter = typeof (window as any).SispoliBridge !== 'undefined'

  function sendToFlutter(type: string, data: Record<string, unknown>) {
    try {
      (window as any).SispoliBridge?.postMessage(JSON.stringify({ type, ...data }))
    } catch {}
  }

  // Copiar texto — funciona em browser e Flutter
  async function handleCopyCaption(item: MediaItem) {
    if (!item.caption_text) return

    if (isFlutter) {
      sendToFlutter('copy_text', { text: item.caption_text })
      setCopiedId(item.id)
      setTimeout(() => setCopiedId(null), 2000)
      return
    }

    try {
      await navigator.clipboard.writeText(item.caption_text)
    } catch {
      // Fallback pra contextos inseguros (HTTP)
      const textarea = document.createElement('textarea')
      textarea.value = item.caption_text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setCopiedId(item.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

  // Download — comportamento diferente por dispositivo
  async function handleDownload(item: MediaItem) {
    setDownloadingId(item.id)

    // Flutter WebView → salva via bridge nativo (galeria/documentos)
    if (isFlutter) {
      sendToFlutter('download_file', { url: item.file_url, fileName: item.file_name, mimeType: item.mime_type })
      setTimeout(() => setDownloadingId(null), 1000)
      return
    }

    // Mobile browser → usa share API pra "salvar em fotos" ou baixa o blob
    if (isMobile) {
      try {
        const res = await fetch(item.file_url)
        const blob = await res.blob()
        const file = new File([blob], item.file_name, { type: blob.type })

        // Tenta usar share pra "Salvar imagem" (iOS mostra opção de salvar)
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: item.title })
          setDownloadingId(null)
          return
        }

        // Fallback mobile: cria link e tenta download
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = item.file_name
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 100)
      } catch {
        // Último fallback: abre em nova aba
        window.open(item.file_url, '_blank')
      } finally {
        setDownloadingId(null)
      }
      return
    }

    // Desktop → download direto via fetch + blob
    try {
      const res = await fetch(item.file_url)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = item.file_name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      window.open(item.file_url, '_blank')
    } finally {
      setDownloadingId(null)
    }
  }

  const [sharingId, setSharingId] = useState<number | null>(null)

  // Compartilhar — baixa o arquivo e abre share nativo com arquivo + texto
  async function handleShare(item: MediaItem) {
    if (isFlutter) {
      sendToFlutter('share_file', { url: item.file_url, fileName: item.file_name, title: item.title, text: item.caption_text || '' })
      return
    }

    setSharingId(item.id)
    try {
      // 1. Baixa o arquivo
      const res = await fetch(item.file_url)
      const blob = await res.blob()
      const file = new File([blob], item.file_name, { type: blob.type || 'application/octet-stream' })

      // 2. Monta o texto (legenda + título)
      const shareText = item.caption_text || item.title

      // 3. Tenta compartilhar com arquivo + texto
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: item.title,
          text: shareText,
        })
      } else if (navigator.share) {
        // Dispositivo não suporta share com arquivo — envia texto + link
        await navigator.share({
          title: item.title,
          text: `${shareText}\n\n${item.file_url}`,
        })
      } else {
        // Sem suporte a share — copia o texto
        await handleCopyCaption({ ...item, caption_text: `${shareText}\n\n${item.file_url}` } as MediaItem)
      }
    } catch {
      // Usuário cancelou
    } finally {
      setSharingId(null)
    }
  }

  // Mostrar compartilhar em mobile (browser ou Flutter)
  const canShare = isFlutter || (typeof navigator !== 'undefined' && !!navigator.share)
  const [viewingItem, setViewingItem] = useState<MediaItem | null>(null)

  const handleRefresh = async () => {
    setPage(1)
    await fetchItems(1, false)
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }

  const formatDate = (d: string) => {
    const date = new Date(d)
    const now = new Date()
    const diffH = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    if (diffH < 1) return 'Agora'
    if (diffH < 24) return `${diffH}h atrás`
    const diffD = Math.floor(diffH / 24)
    if (diffD < 7) return `${diffD}d atrás`
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      {/* Header — desktop only */}
      <div className="h-12 px-4 items-center border-b border-border-subtle hidden lg:flex">
        <FolderDown size={20} className="text-content-muted mr-2" />
        <h1 className="text-base font-semibold text-content-normal">Materiais</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto w-full">
        {/* Filtros */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {(['all', 'image', 'video', 'audio', 'document'] as FilterType[]).map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={clsx(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
                filter === type
                  ? 'bg-primary text-white'
                  : 'bg-surface-secondary text-content-muted hover:bg-surface-hover',
              )}
            >
              {type === 'all' ? 'Todos' : typeLabels[type]}
            </button>
          ))}
        </div>

        {/* Loading inicial */}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex gap-3 p-3 rounded-xl border border-border-subtle animate-pulse">
                <div className="w-16 h-16 rounded-lg bg-gray-200 shrink-0" />
                <div className="flex-1">
                  <div className="h-4 w-2/3 bg-gray-200 rounded mb-1.5" />
                  <div className="h-3 w-1/2 bg-gray-100 rounded mb-2" />
                  <div className="flex gap-3">
                    <div className="h-3 w-12 bg-gray-100 rounded" />
                    <div className="h-3 w-12 bg-gray-100 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <FolderDown size={40} className="text-content-muted mx-auto mb-3 opacity-30" />
            <p className="text-sm text-content-muted">Nenhum material disponível</p>
          </div>
        ) : (
          <>
            {/* Lista */}
            <div className="space-y-3">
              {items.map((item) => {
                const TypeIcon = typeIcons[item.type] || FileText
                const isCopied = copiedId === item.id

                return (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 rounded-xl border border-border-subtle cursor-pointer hover:border-primary/30 transition-all active:scale-[0.99]"
                    onClick={() => setViewingItem(item)}
                  >
                    {/* Thumbnail */}
                    <div className="shrink-0">
                      {item.type === 'image' ? (
                        <img src={item.file_url} alt={item.title} className="w-16 h-16 rounded-xl object-cover" />
                      ) : item.type === 'video' ? (
                        <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-gray-900">
                          <video src={item.file_url} className="w-full h-full object-cover" preload="metadata" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                              <div className="w-0 h-0 border-l-[8px] border-l-gray-800 border-y-[5px] border-y-transparent ml-0.5" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-surface-secondary flex items-center justify-center">
                          <TypeIcon size={24} className="text-content-muted opacity-50" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-content-normal truncate">{item.title}</h3>
                      <div className="flex items-center gap-1.5 mt-1 text-[11px] text-content-muted">
                        <span>{typeLabels[item.type]}</span>
                        <span>·</span>
                        <span>{formatSize(item.file_size)}</span>
                        <span>·</span>
                        <span>{formatDate(item.created_at)}</span>
                        {item.category_name && (
                          <>
                            <span>·</span>
                            <span style={{ color: item.category_color || undefined }}>{item.category_name}</span>
                          </>
                        )}
                      </div>

                      {/* Ações — em mobile: ícones circulares lado a lado com
                          label pequeno embaixo (clean, área de toque 44px,
                          ocupa pouca altura). Em desktop: inline compacto. */}
                      <div className="flex items-start sm:items-center justify-around sm:justify-start gap-2 sm:gap-4 mt-3">
                        <button
                          onClick={e => { e.stopPropagation(); handleDownload(item) }}
                          className="group flex flex-col sm:flex-row items-center sm:gap-1.5 active:scale-95 sm:active:scale-100 transition-transform"
                        >
                          <span className="w-11 h-11 sm:w-auto sm:h-auto flex items-center justify-center rounded-full bg-surface-secondary sm:bg-transparent text-content-normal sm:text-content-muted group-active:bg-surface-active sm:group-hover:text-primary transition-colors">
                            <Download size={18} className="sm:hidden" />
                            <Download size={14} className="hidden sm:inline" />
                          </span>
                          <span className="text-[11px] sm:text-xs mt-1 sm:mt-0 font-medium text-content-muted sm:text-content-muted sm:group-hover:text-primary whitespace-nowrap">
                            {downloadingId === item.id ? 'Salvando…' : 'Salvar'}
                          </span>
                        </button>
                        {item.caption_text && (
                          <button
                            onClick={e => { e.stopPropagation(); handleCopyCaption(item) }}
                            className="group flex flex-col sm:flex-row items-center sm:gap-1.5 active:scale-95 sm:active:scale-100 transition-transform"
                          >
                            <span className="w-11 h-11 sm:w-auto sm:h-auto flex items-center justify-center rounded-full bg-surface-secondary sm:bg-transparent text-content-normal sm:text-content-muted group-active:bg-surface-active sm:group-hover:text-primary transition-colors">
                              {isCopied
                                ? <Check size={18} className="sm:hidden" />
                                : <Copy size={18} className="sm:hidden" />}
                              {isCopied
                                ? <Check size={14} className="hidden sm:inline" />
                                : <Copy size={14} className="hidden sm:inline" />}
                            </span>
                            <span className="text-[11px] sm:text-xs mt-1 sm:mt-0 font-medium text-content-muted sm:group-hover:text-primary whitespace-nowrap">
                              {isCopied ? 'Copiado!' : 'Copiar'}
                            </span>
                          </button>
                        )}
                        {canShare && (
                          <button
                            onClick={e => { e.stopPropagation(); handleShare(item) }}
                            className="group flex flex-col sm:flex-row items-center sm:gap-1.5 active:scale-95 sm:active:scale-100 transition-transform"
                          >
                            <span className="w-11 h-11 sm:w-auto sm:h-auto flex items-center justify-center rounded-full bg-surface-secondary sm:bg-transparent text-content-normal sm:text-content-muted group-active:bg-surface-active sm:group-hover:text-primary transition-colors">
                              <Share2 size={18} className="sm:hidden" />
                              <Share2 size={14} className="hidden sm:inline" />
                            </span>
                            <span className="text-[11px] sm:text-xs mt-1 sm:mt-0 font-medium text-content-muted sm:group-hover:text-primary whitespace-nowrap">
                              {sharingId === item.id ? 'Preparando…' : 'Compartilhar'}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Ver mais */}
            {hasMore && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="secondary"
                  onClick={loadMore}
                  loading={loadingMore}
                >
                  Ver mais materiais
                </Button>
              </div>
            )}

            {!hasMore && items.length > perPage && (
              <p className="text-center text-xs text-content-muted mt-6">Todos os materiais carregados</p>
            )}
          </>
        )}
      </div>

      {/* Modal visualizar — fullscreen no mobile */}
      {viewingItem && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          {/* Header com botão fechar */}
          <div className="flex items-center justify-between px-4 py-3 shrink-0">
            <button
              onClick={() => setViewingItem(null)}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              <span className="text-sm font-medium">Voltar</span>
            </button>
            <p className="text-sm font-medium text-white/80 truncate mx-4 flex-1 text-center">{viewingItem.title}</p>
            <button
              onClick={() => setViewingItem(null)}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          {/* Conteúdo — centralizado */}
          <div className="flex-1 flex items-center justify-center px-4 overflow-auto">
            {viewingItem.type === 'image' ? (
              <img
                src={viewingItem.file_url}
                alt={viewingItem.title}
                className="max-w-full max-h-full object-contain"
              />
            ) : viewingItem.type === 'video' ? (
              <video
                src={viewingItem.file_url}
                controls
                autoPlay
                className="max-w-full max-h-full rounded-xl"
              />
            ) : (
              <div className="bg-white/10 rounded-xl p-8 text-center">
                <FileText size={48} className="mx-auto mb-4 text-white/40" />
                <p className="text-lg font-semibold text-white">{viewingItem.title}</p>
                <p className="text-sm text-white/60 mt-1">{viewingItem.file_name}</p>
              </div>
            )}
          </div>

          {/* Legenda (se tiver) */}
          {viewingItem.caption_text && (
            <div
              className="mx-4 mb-2 p-3 bg-white/10 rounded-xl cursor-pointer active:bg-white/15 transition-colors"
              onClick={() => handleCopyCaption(viewingItem)}
            >
              <p className="text-sm text-white/80 whitespace-pre-wrap line-clamp-4">{viewingItem.caption_text}</p>
              <p className="text-[10px] text-white/40 mt-1">
                {copiedId === viewingItem.id ? 'Copiado!' : 'Toque pra copiar a legenda'}
              </p>
            </div>
          )}

          {/* Rodapé com ações */}
          <div className="flex items-center gap-3 px-4 py-4 border-t border-white/10 shrink-0">
            <button
              onClick={() => handleDownload(viewingItem)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white text-gray-900 text-sm font-semibold active:bg-gray-100 transition-colors"
            >
              <Download size={16} />
              {downloadingId === viewingItem.id ? 'Salvando...' : 'Salvar'}
            </button>
            {canShare && (
              <button
                onClick={() => handleShare(viewingItem)}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/15 text-white text-sm font-semibold active:bg-white/25 transition-colors"
              >
                <Share2 size={16} />
                {sharingId === viewingItem.id ? 'Preparando...' : 'Compartilhar'}
              </button>
            )}
            {viewingItem.caption_text && (
              <button
                onClick={() => handleCopyCaption(viewingItem)}
                className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-white/80 active:bg-white/20 transition-colors shrink-0"
              >
                {copiedId === viewingItem.id ? <Check size={18} /> : <Copy size={18} />}
              </button>
            )}
          </div>
        </div>
      )}
    </PullToRefresh>
  )
}
